/**
 * 组装完整的 Page
 * 将多个 Section 组装成完整的邮件模板
 */

import { BlockManager } from "../../src/utils/BlockManager";
import { BasicType } from "../../src/constants/blockType";
import type { PageElement } from "../types";

export interface PageConfig {
  attributes?: {
    width?: string;
    "background-color"?: string;
    "content-background-color"?: string;
  };
  data?: {
    breakpoint?: string;
    globalAttributes?: Record<string, string>;
    fonts?: Array<{ name: string; href: string }>;
    preheader?: string;
    variables?: Array<{
      name: string;
      value: string;
      label: string;
      type: string;
    }>;
  };
}

export interface AssemblePageInput {
  /** 页面配置 */
  pageConfig: PageConfig;
  /** Section 数组（已生成的 section 元素） */
  sections: any[];
}

export interface AssemblePageOutput {
  success: boolean;
  template?: PageElement;
  error?: string;
}

/**
 * 组装完整的 Page
 */
export function assemblePage(input: AssemblePageInput): AssemblePageOutput {
  try {
    const { pageConfig, sections } = input;

    if (!Array.isArray(sections) || sections.length === 0) {
      throw new Error("sections must be a non-empty array");
    }

    // 验证每个 section
    for (let i = 0; i < sections.length; i++) {
      const section = sections[i];
      if (!section || !section.type) {
        throw new Error(`Section at index ${i} is invalid: missing type`);
      }
      if (
        section.type !== "standard-section" &&
        section.type !== "standard-wrapper"
      ) {
        throw new Error(
          `Section at index ${i} has invalid type: ${section.type}`
        );
      }
    }

    // 创建 Page
    const pageBlockDef = BlockManager.getBlockByType(BasicType.PAGE as any);
    if (!pageBlockDef?.create) {
      throw new Error(`Block type not found: ${BasicType.PAGE}`);
    }

    const pageElement: PageElement = pageBlockDef.create({
      attributes: pageConfig.attributes || {
        width: "600px",
        "background-color": "#f5f5f5",
      },
      data: {
        breakpoint: pageConfig.data?.breakpoint || "480px",
        globalAttributes: pageConfig.data?.globalAttributes,
        fonts: pageConfig.data?.fonts,
        preheader: pageConfig.data?.preheader,
        variables: pageConfig.data?.variables,
      },
      children: sections,
    });

    return {
      success: true,
      template: pageElement,
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : String(error),
    };
  }
}

export const ASSEMBLE_PAGE_TOOL = {
  name: "assemble_page",
  description: `将多个 Section 组装成完整的邮件模板 Page。

工作流程:
1. 先使用 generate_section 逐个生成各个区块
2. 收集所有生成的 section
3. 使用 assemble_page 组装成完整模板

输入:
- pageConfig: 页面配置
  - attributes: { width, background-color, content-background-color }
  - data: { breakpoint, globalAttributes, fonts, preheader }
- sections: 已生成的 Section 元素数组

示例:
{
  "pageConfig": {
    "attributes": { "width": "600px", "background-color": "#f5f5f5" },
    "data": { "breakpoint": "480px" }
  },
  "sections": [
    { "type": "standard-section", "data": {}, "attributes": {...}, "children": [...] },
    { "type": "standard-section", "data": {}, "attributes": {...}, "children": [...] }
  ]
}

输出:
- success: boolean
- template: 完整的 Page 元素
- error: 错误信息`,
  inputSchema: {
    type: "object" as const,
    properties: {
      pageConfig: {
        type: "object",
        description: "页面配置",
        properties: {
          attributes: {
            type: "object",
            description: "页面属性",
          },
          data: {
            type: "object",
            description: "页面数据",
          },
        },
      },
      sections: {
        type: "array",
        description: "已生成的 Section 元素数组",
        items: { type: "object" },
      },
    },
    required: ["pageConfig", "sections"],
  },
};
